import type { VariantProps } from 'class-variance-authority';

import * as React from 'react';
import { Slot } from 'radix-ui';
import { Loader2 } from 'lucide-react';
import { cva } from 'class-variance-authority';

import { cn } from '@shared/utils/cn';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-z8 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:data-loading:bg-primary disabled:data-loading:text-primary-foreground disabled:data-loading:opacity-70',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:data-loading:bg-destructive disabled:data-loading:text-white disabled:data-loading:opacity-70',
        outline:
          'border border-input bg-background hover:border-foreground hover:bg-accent hover:shadow-[0_0_0_0.75px_currentColor]',
        success:
          'bg-success text-white hover:bg-success/90 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:data-loading:bg-success disabled:data-loading:text-white disabled:data-loading:opacity-70',
        warning:
          'bg-warning text-white hover:bg-warning/90 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:data-loading:bg-warning disabled:data-loading:text-white disabled:data-loading:opacity-70',
        soft: 'bg-primary/16 text-primary hover:bg-primary/32',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      // Tailles du thème Minimal : small 30 / medium 36 / large 48 / xLarge 56 (icon = IconButton, 40)
      size: {
        sm: 'h-[30px] px-2 text-[13px]',
        default: 'h-9 px-3',
        lg: 'h-12 px-4 text-[15px]',
        xl: 'h-14 px-4 text-[15px]',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      data-loading={loading || undefined}
      data-slot="button"
      disabled={disabled || loading}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Loader2 className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );
};

export { Button, buttonVariants };
